import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../translations';
import { CheckIcon, PackageIcon } from '../../ui/icons';
import { Banner } from '../ui/Banner';
import ScanSources from '../scanner/ScanSources';
import useBarcodeScan from '../../utils/useBarcodeScan';
import { findEquipmentByCode, labelCodeFor } from '../../utils/equipmentCode';
import { fetchHandover, saveHandover } from '../scanner/scanApi';

// Wydanie i zwrot sprzętu per sztuka (2026-09-24, migracja 045).
//
// Status rezerwacji wynika z dat, więc „Zakończona" nie mówiła, czy narty wróciły. Tu każda
// sztuka dostaje godzinę i osobę wydania oraz zwrotu. Skan (czytnik USB, aparat, telefon)
// ZAPISUJE OD RAZU — przy ladzie nikt nie pamięta o przycisku „Zapisz" na końcu serii, a
// niezapisany stan ginąłby przy zamknięciu okna rezerwacji.
//
// Zeskanowanie sztuki spoza rezerwacji jest błędem na głos, nie cichym pominięciem: to dokładnie
// ta pomyłka, przed którą skanowanie ma chronić (klient dostaje nie te narty).

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Tabs = styled.div`
  display: inline-flex;
  border: 1px solid var(--input-border);
  border-radius: var(--r-pill);
  padding: 3px;
  gap: 2px;

  button {
    border: 0;
    background: transparent;
    color: var(--text-muted);
    padding: 7px 14px;
    border-radius: var(--r-pill);
    font-size: var(--fs-sm);
    font-weight: 600;
    cursor: pointer;
  }
  button[aria-selected='true'] { background: var(--primary); color: var(--primary-contrast); }
  button:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--fs-sm);
  color: var(--text-muted);

  .bar { flex: 1; height: 6px; border-radius: 3px; background: var(--hover-bg); overflow: hidden; }
  .bar > span { display: block; height: 100%; background: var(--success); transition: width var(--dur) var(--ease); }
  strong { color: var(--text); font-variant-numeric: tabular-nums; }
`;

const Feedback = styled.div`
  padding: 10px 12px;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  font-weight: 600;
  background: ${(p) => (p.$tone === 'ok' ? 'var(--success-bg)' : p.$tone === 'warn' ? 'var(--warning-bg)' : 'var(--danger-bg)')};
  color: ${(p) => (p.$tone === 'ok' ? 'var(--success)' : p.$tone === 'warn' ? 'var(--warning)' : 'var(--danger)')};
  border: 1px solid ${(p) => (p.$tone === 'ok' ? 'var(--success-border)' : p.$tone === 'warn' ? 'var(--warning-border)' : 'var(--danger-border)')};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--r-md);
  border: 1px solid ${(p) => (p.$done ? 'var(--success-border)' : 'var(--input-border)')};
  background: ${(p) => (p.$done ? 'var(--success-bg)' : 'var(--panel-bg)')};

  .mark {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: ${(p) => (p.$done ? 'var(--success)' : 'var(--hover-bg)')};
    color: ${(p) => (p.$done ? '#fff' : 'var(--text-muted)')};
  }
  .info { flex: 1; min-width: 0; }
  .name { font-weight: 600; font-size: var(--fs-sm); color: var(--text); }
  .meta { font-size: var(--fs-xs); color: var(--text-muted); overflow-wrap: anywhere; }
  code { font-size: var(--fs-xs); }
`;

const RowButton = styled.button`
  flex-shrink: 0;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text);
  border-radius: var(--r-md);
  padding: 6px 12px;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: progress; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const HeldNote = styled.div`
  margin-top: 2px;
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--warning);
`;

const BulkButton = styled(RowButton)`
  align-self: flex-start;
  padding: 8px 14px;
  font-size: var(--fs-sm);
`;

const LOCALES = { pl: 'pl-PL', en: 'en-GB', de: 'de-DE' };
const FIELD = { pickup: ['picked_up_at', 'picked_up_by_name'], return: ['returned_at', 'returned_by_name'] };

// `onChange(items, { saved })` (X.1): stan sztuk po wczytaniu i po każdym zapisie. Okno szczegółów
// liczy z niego status rezerwacji, a po zapisie (`saved`) odświeża listy w kalendarzu.
export default function HandoverPanel({ reservationId, active = true, onChange }) {
  const { t, currentLanguage } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [phase, setPhase] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [feedback, setFeedback] = useState(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const fmt = useCallback((iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(LOCALES[currentLanguage] || 'pl-PL', { dateStyle: 'short', timeStyle: 'short' }); } catch (_) { return ''; }
  }, [currentLanguage]);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const d = await fetchHandover(reservationId);
      setData(d);
      if (onChangeRef.current) onChangeRef.current(d.items, { saved: false });
      setPhase((prev) => prev || (d.items.length && d.items.every((i) => i.picked_up_at) ? 'return' : 'pickup'));
    } catch (_) {
      setLoadError(true);
    }
  }, [reservationId]);

  useEffect(() => { setData(null); setPhase(null); setFeedback(null); load(); }, [load]);

  const items = useMemo(() => (data ? data.items : []), [data]);
  // N.1: sztuki jeszcze u poprzedniego klienta. Sama informacja: wydania nie blokuje, bo zwrot mógł
  // nie zostać zapisany, a skanowana sztuka i tak jest w ręku.
  const heldElsewhere = useMemo(() => new Set(data?.heldElsewhere || []), [data]);
  // Kształt, który rozumie `findEquipmentByCode` (id sztuki, numer, kod producenta).
  const codeItems = useMemo(() => items.map((i) => ({ ...i, id: i.equipment_id })), [items]);
  const [atField, byField] = FIELD[phase || 'pickup'];
  const doneCount = items.filter((i) => i[atField]).length;

  // Sztuki, których zapis właśnie leci. Ref, nie stan: dwa skany tej samej naklejki w odstępie
  // krótszym niż odpowiedź serwera muszą się o sobie dowiedzieć bez czekania na render.
  const inFlightRef = useRef(new Set());

  const apply = useCallback(async (equipmentIds, done) => {
    equipmentIds.forEach((id) => inFlightRef.current.add(id));
    setBusy((prev) => new Set([...prev, ...equipmentIds]));
    try {
      const d = await saveHandover(reservationId, { phase, equipmentIds, done });
      setData(d);
      if (onChangeRef.current) onChangeRef.current(d.items, { saved: true });
      return d;
    } finally {
      equipmentIds.forEach((id) => inFlightRef.current.delete(id));
      setBusy((prev) => { const n = new Set(prev); equipmentIds.forEach((id) => n.delete(id)); return n; });
    }
  }, [reservationId, phase]);

  // X.1: serwer odrzuca zwrot przed dniem początku rezerwacji (lib/reservationEquipment.js) — mówimy
  // dlaczego, zamiast ogólnego „nie udało się zapisać", po którym ktoś próbowałby w kółko.
  const bladZapisu = useCallback((e) => (
    e?.response?.data?.error === 'return_before_start' ? t('handover.returnBeforeStart') : t('handover.saveError')
  ), [t]);

  const handleScan = useCallback(async (code) => {
    if (!data || data.cancelled || !phase) return { tone: 'error', text: t('handover.notAvailable') };
    const hit = findEquipmentByCode(code, codeItems);
    let wynik;
    if (!hit) {
      wynik = { tone: 'error', text: t('handover.scan.notInReservation').replace('{code}', code) };
    } else if (hit[atField] || inFlightRef.current.has(hit.equipment_id)) {
      wynik = {
        tone: 'warn',
        text: t(`handover.scan.already.${phase}`).replace('{name}', hit.name).replace('{time}', fmt(hit[atField] || new Date().toISOString())),
      };
    } else {
      try {
        const d = await apply([hit.equipment_id], true);
        const n = d.items.filter((i) => i[atField]).length;
        wynik = {
          tone: 'ok',
          text: t(`handover.scan.done.${phase}`).replace('{name}', hit.name).replace('{n}', String(n)).replace('{total}', String(d.items.length)),
        };
      } catch (e) {
        wynik = { tone: 'error', text: bladZapisu(e) };
      }
    }
    setFeedback(wynik);
    return wynik;
  }, [data, phase, codeItems, atField, apply, fmt, t, bladZapisu]);

  useBarcodeScan(handleScan, { enabled: active && !!data && !data.cancelled && items.length > 0 });

  const toggle = async (item) => {
    try {
      await apply([item.equipment_id], !item[atField]);
      setFeedback(null);
    } catch (e) {
      setFeedback({ tone: 'error', text: bladZapisu(e) });
    }
  };

  const markAll = async () => {
    const rest = items.filter((i) => !i[atField]).map((i) => i.equipment_id);
    if (!rest.length) return;
    try {
      await apply(rest, true);
      setFeedback({ tone: 'ok', text: t(`handover.allDone.${phase}`) });
    } catch (e) {
      setFeedback({ tone: 'error', text: bladZapisu(e) });
    }
  };

  if (loadError) {
    return (
      <Banner tone="danger" action={{ label: t('common.retry'), onClick: load }} data-cy="handover-load-error">
        {t('handover.loadError')}
      </Banner>
    );
  }
  if (!data) return <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>{t('common.loading')}</div>;
  if (!items.length) return <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>{t('handover.noItems')}</div>;

  const pct = Math.round((doneCount / items.length) * 100);

  return (
    <Panel data-cy="handover-panel" data-phase={phase}>
      {data.cancelled && <Banner tone="warning" compact>{t('handover.cancelled')}</Banner>}

      <Head>
        <Tabs role="tablist" aria-label={t('handover.title')}>
          {['pickup', 'return'].map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={phase === p}
              onClick={() => { setPhase(p); setFeedback(null); }}
              data-cy={`handover-tab-${p}`}
            >
              {t(`handover.tabs.${p}`)}
            </button>
          ))}
        </Tabs>
        {!data.cancelled && <ScanSources onScan={handleScan} compact dataCyPrefix="handover-scan" cameraTitle={t(`handover.cameraTitle.${phase}`)} />}
      </Head>

      <Progress>
        <div className="bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
        <span data-cy="handover-progress">
          {t(`handover.progress.${phase}`).replace('{n}', String(doneCount)).replace('{total}', String(items.length))}
        </span>
      </Progress>

      {!data.cancelled && (
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>{t('handover.scanHint')}</div>
      )}

      {feedback && (
        <Feedback $tone={feedback.tone} role="status" aria-live="polite" data-cy="handover-feedback" data-tone={feedback.tone}>
          {feedback.text}
        </Feedback>
      )}

      <List>
        {items.map((i) => {
          const done = !!i[atField];
          return (
            <Row key={i.equipment_id} $done={done} data-cy="handover-item" data-done={done} data-equipment-id={i.equipment_id}>
              <span className="mark" aria-hidden="true">{done ? <CheckIcon size={15} /> : <PackageIcon size={15} />}</span>
              <div className="info">
                <div className="name">{i.name}{i.brand ? ` · ${i.brand}` : ''}</div>
                <div className="meta">
                  <code>{labelCodeFor({ ...i, id: i.equipment_id })}</code>
                  {done && ` · ${fmt(i[atField])}${i[byField] ? ` · ${i[byField]}` : ''}`}
                </div>
                {phase === 'pickup' && !done && heldElsewhere.has(i.equipment_id) && (
                  <HeldNote data-cy="handover-held-elsewhere">{t('handover.heldElsewhere')}</HeldNote>
                )}
              </div>
              {!data.cancelled && (
                <RowButton
                  type="button"
                  onClick={() => toggle(i)}
                  disabled={busy.has(i.equipment_id)}
                  data-cy="handover-item-toggle"
                >
                  {done ? t('handover.undo') : t(`handover.mark.${phase}`)}
                </RowButton>
              )}
            </Row>
          );
        })}
      </List>

      {!data.cancelled && doneCount < items.length && (
        <BulkButton type="button" onClick={markAll} disabled={busy.size > 0} data-cy="handover-mark-all">
          {t(`handover.markAll.${phase}`).replace('{n}', String(items.length - doneCount))}
        </BulkButton>
      )}
    </Panel>
  );
}
